import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import styles from "./NotificationCenter.module.css";

const API         = "https://api-backend.full-trucking-service.com/api";
const POLL_MS     = 5 * 60 * 1000;
const DISMISS_TTL = 4 * 60 * 60 * 1000;
const APPEAR_MS   = 1500;  // interval între apariția fiecărei notificări
const MAX_VISIBLE = 5;

const ICONS       = { error: "🔴", warning: "🟡", info: "🔵", task: "📋" };
const SECTION_MAP = { driver: "drivers", truck: "trucks", load: "load-manager", task: "dashboard" };
const PRIORITY    = { error: 0, warning: 1, task: 2, info: 3 };

const NotificationCenter = ({ onNavigateToSection }) => {
  const [queue, setQueue]     = useState([]); // așteptare
  const [visible, setVisible] = useState([]); // afișate pe ecran
  const timerRef              = useRef(null);

  // ── localStorage ──────────────────────────────────────
  const getDismissed = useCallback(() => {
    const raw = JSON.parse(localStorage.getItem("dismissedNotifs") || "{}");
    const now = Date.now();
    const clean = Object.fromEntries(
      Object.entries(raw).filter(([, ts]) => now - ts < DISMISS_TTL)
    );
    localStorage.setItem("dismissedNotifs", JSON.stringify(clean));
    return clean;
  }, []);

  const dismiss = useCallback((id) => {
    const d = getDismissed();
    d[id] = Date.now();
    localStorage.setItem("dismissedNotifs", JSON.stringify(d));
    // scoate din visible
    setVisible(prev => prev.filter(n => n.id !== id));
    // adaugă următoarea din queue în visible
    setQueue(prev => {
      if (prev.length === 0) return prev;
      const [next, ...rest] = prev;
      setVisible(v => v.length < MAX_VISIBLE ? [...v, next] : v);
      return rest;
    });
  }, [getDismissed]);

  // ── fetch ──────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      if (!token) return;

      const { data } = await axios.get(`${API}/notifications/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const dismissed = getDismissed();
      const fresh = data
        .filter(n => !dismissed[n.id])
        .sort((a, b) => (PRIORITY[a.type] ?? 9) - (PRIORITY[b.type] ?? 9));

      if (fresh.length === 0) return;

      setVisible(currentVisible => {
        setQueue(currentQueue => {
          const existingIds = new Set([
            ...currentVisible.map(n => n.id),
            ...currentQueue.map(n => n.id),
          ]);
          const newItems = fresh.filter(n => !existingIds.has(n.id));
          if (newItems.length === 0) return currentQueue;

          // câte locuri libere în visible
          const freeSlots = MAX_VISIBLE - currentVisible.length;
          const goVisible = newItems.slice(0, freeSlots);
          const goQueue   = newItems.slice(freeSlots);

          // programează apariția pe rând pentru goVisible
          goVisible.forEach((item, i) => {
            setTimeout(() => {
              setVisible(v =>
                v.find(x => x.id === item.id) ? v : [...v, item]
              );
            }, i * APPEAR_MS);
          });

          return [...currentQueue, ...goQueue];
        });
        return currentVisible;
      });
    } catch (err) {
      console.error("NotificationCenter fetch error:", err);
    }
  }, [getDismissed]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, POLL_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timerRef.current);
    };
  }, [fetchAll]);

  const handleClick = useCallback((n) => {
    onNavigateToSection?.(SECTION_MAP[n.category], n.entityId);
    dismiss(n.id);
  }, [onNavigateToSection, dismiss]);

  if (visible.length === 0) return null;

  return createPortal(
    <div className={styles.stack}>
      {visible.map(n => (
        <div
          key={n.id}
          className={`${styles.card} ${styles[n.type]}`}
          onClick={() => handleClick(n)}
          role="alert"
        >
          <span className={styles.icon}>{ICONS[n.type] ?? "🔔"}</span>
          <span className={styles.msg}>{n.message}</span>
          <button
            className={styles.close}
            onClick={e => { e.stopPropagation(); dismiss(n.id); }}
            aria-label="Dismiss"
          >×</button>
        </div>
      ))}
    </div>,
    document.body
  );
};

export default NotificationCenter;